import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api from "../../lib/api";
import { money } from "../../lib/format";

const SOURCES = [
  { value: "salary", label: "Salary" },
  { value: "bonus", label: "Bonus" },
  { value: "side", label: "Side Income" },
];

export default function IncomeManager({ open, onOpenChange, month, currency, onChanged, readOnly }) {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("salary");
  const [label, setLabel] = useState("");
  const [expected, setExpected] = useState("");
  const [recvFor, setRecvFor] = useState(null);
  const [recvAmt, setRecvAmt] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/income?month=${month}`);
    setItems(data);
  }, [month]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async () => {
    if (readOnly) return toast.error("View-only account — editing is disabled.");
    if (!label || !expected) return toast.error("Enter label and expected amount");
    await api.post("/income", { source, label, expected: Number(expected), month });
    setLabel(""); setExpected("");
    toast.success("Income stream added");
    load(); onChanged();
  };

  const receive = async (id) => {
    if (readOnly) return toast.error("View-only account — editing is disabled.");
    await api.post(`/income/${id}/receive`, { actual: Number(recvAmt) });
    setRecvFor(null); setRecvAmt("");
    toast.success("Income marked received");
    load(); onChanged();
  };

  const del = async (id) => { if (readOnly) return toast.error("View-only account — editing is disabled."); await api.delete(`/income/${id}`); load(); onChanged(); };

  const expTotal = items.reduce((s, i) => s + Number(i.expected), 0);
  const actTotal = items.reduce((s, i) => s + Number(i.actual), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader><DialogTitle className="font-display">Income Tracker</DialogTitle><DialogDescription>Track expected vs received income across salary, bonus and side income.</DialogDescription></DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Expected</p>
            <p data-testid="income-tracker-expected" className="font-mono-nums text-lg font-bold text-slate-800">{money(expTotal, currency)}</p>
          </div>
          <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
            <p className="text-xs text-emerald-500 uppercase tracking-wider">Received</p>
            <p data-testid="income-tracker-total" className="font-mono-nums text-lg font-bold text-emerald-700">{money(actTotal, currency)}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-end bg-slate-50 p-3 rounded-xl">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="income-source-select" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="income-label-input" placeholder="Label e.g. Monthly Salary" className="flex-1 min-w-[150px]" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Input data-testid="income-expected-input" type="number" placeholder="Expected" className="w-28 font-mono-nums" value={expected} onChange={(e) => setExpected(e.target.value)} />
          <Button data-testid="income-add-button" onClick={add} disabled={readOnly} className="bg-orange-600 hover:bg-orange-700 text-white"><Plus className="w-4 h-4" /></Button>
        </div>

        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} data-testid={`income-row-${i.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
              <div className="flex-1">
                <p className="font-medium text-slate-800 text-sm">{i.label}</p>
                <p className="text-xs text-slate-400 capitalize">{i.source} · expected {money(i.expected, currency)}</p>
              </div>
              {i.received ? (
                <span className="font-mono-nums font-semibold text-emerald-700 text-sm">{money(i.actual, currency)}</span>
              ) : recvFor === i.id ? (
                <div className="flex items-center gap-1">
                  <Input autoFocus type="number" data-testid={`income-receive-input-${i.id}`} placeholder={String(i.expected)} className="w-24 h-8 font-mono-nums" value={recvAmt} onChange={(e) => setRecvAmt(e.target.value)} />
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => receive(i.id)} data-testid={`income-receive-confirm-${i.id}`}>Mark</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setRecvFor(i.id); setRecvAmt(String(i.expected)); }} data-testid={`income-receive-${i.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Received</Button>
              )}
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(i.id)} data-testid={`income-delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-sm text-slate-400 py-6">No income streams yet. Add salary, bonus or side income.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
